import * as React from 'react';
import { MdBusiness, MdClose, MdLocationOn, MdPeople, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../../Components/Table/DataGrid';
import { APPRoutes } from '../../../Utils/Route';
import { getAllOrganizationAnalytics } from '../../../Services/AnalyticsService';
import { useAuth } from '../../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../../Components/Common/PageTitle';

const getOrganizationName = (row) =>
  row.OrganizationName || row.organization_name || row.group_office_name || row.Organization?.Name || row.Organization?.OrganizationName || '';

const getClientName = (row) =>
  row.account_name || row.ClientName || row.client_name || row.Client?.Name || row.Client?.ClientName || '';

const getFacilityCount = (row) =>
  typeof row.facilities_count === 'number'
    ? row.facilities_count
    : typeof row.FacilityCount === 'number'
    ? row.FacilityCount
    : Array.isArray(row.pos_list)
    ? row.pos_list.length
    : 0;

export default function AnalyticsOrganizationList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canList = permissionHas(userPermissionCodes, PermissionCodes.ANALYTICS_ORGANIZATIONS_LIST, decodedToken);
  const canViewDetails = permissionHas(userPermissionCodes, PermissionCodes.ANALYTICS_ORGANIZATIONS_VIEW, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);
  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const [summary, setSummary] = React.useState({
    total_organizations: 0,
    total_clients: 0,
    total_facilities: 0,
  });

  const loadOrganizations = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getAllOrganizationAnalytics(token);
      const items = response?.data?.organizations || [];
      const summaryData = response?.data?.summary || {};

      setRows(Array.isArray(items) ? items : []);
      setSummary({
        total_organizations: Number(summaryData.total_organizations) || 0,
        total_clients: Number(summaryData.total_clients) || 0,
        total_facilities: Number(summaryData.total_facilities) || 0,
      });
    } catch (error) {
      console.error('Error loading analytics organizations:', error);
      setRows([]);
      setSummary({
        total_organizations: 0,
        total_clients: 0,
        total_facilities: 0,
      });
      toast.error('Unable to load organization analytics.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const org = getOrganizationName(row).toLowerCase();
      const client = getClientName(row).toLowerCase();
      return !term || org.includes(term) || client.includes(term);
    });
  }, [rows, search]);

  const stats = React.useMemo(() => {
    const list = [
      {
        label: 'Organizations',
        value: summary.total_organizations,
        icon: <MdBusiness size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
    ];

    if (isSuperAdmin) {
      list.push({
        label: 'Clients',
        value: summary.total_clients,
        icon: <MdPeople size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      });
    }

    list.push({
      label: 'Facilities',
      value: summary.total_facilities,
      icon: <MdLocationOn size={22} color="var(--hc-icon-amber-text)" />,
      iconBg: 'var(--hc-icon-amber-bg)',
      accentClass: 'border-warning',
      valueClass: 'text-warning',
    });

    return list;
  }, [summary, isSuperAdmin]);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'organization',
        label: 'Organization',
        minWidth: 220,
        format: (_value, row) =>
          canViewDetails ? (
            <button
              type="button"
              className="btn p-0 text-start text-dark border-0 bg-transparent"
              style={{ font: 'inherit' }}
              onClick={() =>
                navigate(APPRoutes.ANALYTICS_ORGANIZATION_VIEW.replace(':id', row.id ?? row.Id), { state: { organization: row } })
              }
            >
              {getOrganizationName(row) || '—'}
            </button>
          ) : (
            <span>{getOrganizationName(row) || '—'}</span>
          ),
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'client',
        label: 'Client',
        minWidth: 180,
        format: (_value, row) => getClientName(row) || <span className="text-secondary">—</span>,
      });
    }

    cols.push(
      {
        id: 'facilities',
        label: 'Facilities',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => getFacilityCount(row),
      },
    );

    if (canViewDetails) {
      cols.push({
        id: 'actions',
        label: 'Action',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <button
              type="button"
              title="View"
              className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
              onClick={() =>
                navigate(APPRoutes.ANALYTICS_ORGANIZATION_VIEW.replace(':id', row.id ?? row.Id), { state: { organization: row } })
              }
            >
              <MdVisibility size={16} />
            </button>
          </div>
        ),
      });
    }

    return cols;
  }, [canViewDetails, navigate, isSuperAdmin]);

  return (
    <div>
        <PageTitle title="Organization - Aegis 360"/>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Analytics Organizations</h4>
          <p className="text-secondary mb-0 small">
            View organization analytics with facility counts and tenant details.
          </p>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
            <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
              <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
              >
                {stat.icon}
              </div>
              <div className="d-flex flex-column fw-bold lh-sm">
                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                  {stat.label}
                </span>
                <span className={`fw-bold ${stat.valueClass} fs-5`}>
                  {stat.value}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3 align-items-center">
          <div className="col-12">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input w-100"
                placeholder="Search by organization or client..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: search ? '36px' : '12px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {loading ? (
          <div>
            {[...Array(8)].map((_, index) => (
              <div key={index} className="d-flex gap-3 align-items-center mb-3">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '30%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
              </div>
            ))}
          </div>
        ) : (
          <DataGrid
            columns={columns}
            rows={filteredRows}
            defaultRowsPerPage={10}
            stickyHeader
            getRowId={(row) => row.id ?? row.Id ?? `${row.organization_name}-${row.account_name}`}
          />
        )}
      </div>
    </div>
  );
}
